import { useState } from 'react';
import { toast } from 'sonner';
import { Trash2, AlertCircle } from 'lucide-react';
import { Button } from '../ui/Button';
import { ConfirmModal } from '../ui/ConfirmModal';
import { api, ApiError } from '../../lib/api';
import type { PersonApi } from '../../routes/admin/person/types';

export function PseudonymisePanel({
  personId,
  onDone,
}: {
  personId: string;
  onDone: () => void;
}) {
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  async function run(reason?: string) {
    // The reason textarea is required as a typed-confirmation step,
    // matching the previous two-browser-prompt flow's intent. We don't
    // store the reason today but the audit log will pick it up if the
    // server endpoint ever accepts it.
    void reason;
    setRunning(true);
    setError(null);
    try {
      // Endpoint name stays "pseudonymise": that's the legal term and
      // the audit log uses it. The button just speaks plainer English.
      await api<{ person: PersonApi }>(
        `/api/admin/people/${personId}/pseudonymise`,
        { method: 'POST', body: JSON.stringify({}) },
      );
      toast.success('Record anonymised');
      setOpen(false);
      onDone();
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'anonymise failed';
      setError(msg);
      toast.error(msg);
    } finally {
      setRunning(false);
    }
  }

  return (
    <section className="overflow-hidden rounded-lg border-2 border-danger/25 bg-danger-soft/15 shadow-sm">
      <div className="flex items-center gap-2 border-b border-danger/25 bg-danger-soft/15/60 px-5 py-3">
        <Trash2 className="h-4 w-4 text-danger-soft-fg" />
        <h2 className="text-sm font-semibold text-danger-soft-fg">Anonymise (GDPR erasure)</h2>
      </div>
      <div className="p-5 sm:p-6">
        <p className="text-sm text-danger-soft-fg">
          Wipes the personal details on this record: name, email,
          employee ID, notes. The signed event history stays for audit
          but the row no longer identifies anyone.{' '}
          <strong>This cannot be undone.</strong>
        </p>
        {error ? (
          <div className="mt-3 flex items-start gap-2 rounded-md bg-surface px-3 py-2 text-sm text-danger-soft-fg">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
        ) : null}
      </div>
      <div className="flex justify-end border-t border-danger/25 bg-danger-soft/15/40 px-5 py-3">
        <Button
          variant="destructive"
          size="md"
          iconLeft={<Trash2 className="h-4 w-4" />}
          loading={running}
          onClick={() => setOpen(true)}
        >
          Anonymise this record
        </Button>
      </div>

      <ConfirmModal
        open={open}
        tone="destructive"
        icon={Trash2}
        title="Anonymise this record?"
        message={
          <>
            Wipes name, email, employee ID and notes. The signed event
            history stays for audit; the row no longer identifies the
            person. <strong>This cannot be undone.</strong>
          </>
        }
        reasonLabel="Reason (required for the audit log)"
        reasonPlaceholder="e.g. GDPR §17 erasure request received via email 2026-05-26"
        confirmLabel="Anonymise"
        cancelLabel="Cancel"
        onConfirm={run}
        onClose={() => setOpen(false)}
      />
    </section>
  );
}
